/**
 * CONNECTOR REGISTRY (migration 0083) — the one contract behind every integration surface.
 *
 * Before this, "connectivity" was a modal with four hard-coded cards and `alert()` handlers
 * (components/ConnectivityModal.tsx). Adding a fifth integration meant writing a fifth card, a
 * fifth settings screen and a fifth set of fields. This file replaces that with a TYPE REGISTRY:
 * each connector type declares its own configuration fields, and ONE reusable form renders them
 * while ONE server-side validator checks them. A new connector type is a new entry here plus a
 * `connect` implementation — never a new page.
 *
 * Three things are deliberately in this file rather than in the API or the web app:
 *
 *   1. FIELDS. The dynamic form and `validateConnectorConfig` read the same definitions, so a
 *      required field the UI enforces is a required field the server enforces. They cannot drift.
 *
 *   2. `secret: true`. A field marked secret is stored in org_connectors.secrets and NEVER
 *      returned by the API — the same split adminProviders.ts uses for the id-verification key.
 *      The registry is what tells the API which of a type's fields that applies to, so a new
 *      connector's password cannot accidentally be stored in the readable `config` column.
 *
 *   3. `provisioning`. 'native' means TalentHop operates the integration. 'external_subscription'
 *      means the CUSTOMER brings their own account/seat/API access (LinkedIn Jobs, Naukri) and we
 *      only hold the credentials. The UI states which is which rather than implying we resell a
 *      job board we do not.
 *
 * HONESTY CONTRACT (docs/GROUND_RULES.md). `wired: false` marks a type whose configuration is
 * stored but whose transport is NOT implemented — exactly the treatment InviteModes gives its
 * unshipped lanes. Such a connector can be configured and saved, and its test reports honestly
 * that the configuration is recorded and the transport is not live yet. It never claims to be
 * connected, and it never silently does nothing while showing a green dot.
 */
import { z } from "zod";
// ── lifecycle ────────────────────────────────────────────────────────────────
/**
 * The common lifecycle every connector type moves through, whatever it talks to.
 *
 *   not_configured  the row exists (or the type is merely offered) but has no usable config
 *   configured      required fields are present and valid; nothing has talked to the far end
 *   connected       a test succeeded — the far end answered
 *   active          connected AND switched on; this is the only state that processes traffic
 *   inactive        deliberately switched off by an administrator; config is retained
 *   error           the last test or the last real attempt failed; needs attention
 *
 * `active` vs `inactive` is the administrator's enable/disable switch. `connected` vs `error` is
 * the machine's verdict. Keeping them in ONE column is what lets a single status pill tell the
 * whole story, but it means enabling a connector that has never passed a test leaves it at
 * `configured` — see `nextStatus`.
 */
export const CONNECTOR_STATUSES = [
    "not_configured",
    "configured",
    "connected",
    "active",
    "inactive",
    "error",
];
export const CONNECTOR_STATUS_LABELS = {
    not_configured: "Not configured",
    configured: "Configured",
    connected: "Connected",
    active: "Active",
    inactive: "Inactive",
    error: "Error",
};
/** Only an `active` connector is allowed to process traffic. Everything else is inert. */
export const isConnectorLive = (status) => status === "active";
// ── field definitions ────────────────────────────────────────────────────────
export const CONNECTOR_FIELD_TYPES = [
    "text",
    "password",
    "email",
    "url",
    "number",
    "boolean",
    "select",
    "textarea",
];
// ── connector types ──────────────────────────────────────────────────────────
export const CONNECTOR_TYPE_KEYS = [
    "email_receive",
    "gmail",
    "outlook",
    "send_mail",
    "webhook",
    "crm",
    "linkedin_jobs",
    "naukri",
];
/** What the connector is FOR — drives the grouping on the Connections page. */
export const CONNECTOR_CATEGORIES = ["intake", "outbound", "ats", "job_board"];
export const CONNECTOR_CATEGORY_LABELS = {
    intake: "Candidate intake",
    outbound: "Outbound mail",
    ats: "ATS & CRM",
    job_board: "Job boards",
};
/**
 * Who owns the account at the far end.
 *
 *   native                 TalentHop operates it. Nothing to buy; configure and go.
 *   external_subscription  the CUSTOMER brings their own account, seat or API access. We store
 *                          the credentials and drive the integration; we do not resell the
 *                          service and cannot provision it on their behalf.
 */
export const CONNECTOR_PROVISIONING = ["native", "external_subscription"];
/** How the far end authenticates us. `none` means the config carries no credential at all. */
export const CONNECTOR_AUTH_KINDS = ["none", "api_key", "basic", "oauth", "shared_secret"];
/**
 * INBOUND EMAIL — the shared domain behind every job-specific receiving address.
 *
 * One catch-all mailbox for the whole platform, never a mailbox per job: a job's address is a
 * LOCAL-PART pattern (`job-<code>@<domain>`) that the catch-all collects and the ingestion
 * endpoint routes by the ORIGINAL recipient. Adding a job costs nothing at the mail provider.
 */
export const INBOUND_EMAIL_DOMAIN = "jobs.talenthopai.com";
export const INBOUND_CATCH_ALL = `inbound@${INBOUND_EMAIL_DOMAIN}`;
/** The local-part prefix every job address carries. Kept here so parse and format agree. */
export const JOB_EMAIL_PREFIX = "job-";
export const CONNECTOR_TYPES = [
    {
        key: "email_receive",
        label: "Email receiving",
        description: "Give every job its own address. Applications forwarded there become candidates on that job automatically.",
        category: "intake",
        provisioning: "native",
        auth: "shared_secret",
        wired: true,
        singleton: true,
        fields: [
            {
                key: "receiveApplications",
                label: "Receive candidate applications by email",
                type: "boolean",
                default: true,
                help: "Forward candidate application emails to the address above to automatically process candidates for this requirement.",
            },
            {
                key: "autoMatch",
                label: "Run AI matching on arrival",
                type: "boolean",
                default: false,
                help: "Automatically match the scanned resume against this requirement. AI matching uses the configured matching credit.",
            },
            {
                key: "requestMissingResume",
                label: "Ask for a resume when none is attached",
                type: "boolean",
                default: true,
                help: "Automatically request a resume from candidates who don't include one.",
            },
            {
                key: "allowedSenderDomains",
                label: "Restrict to sender domains",
                type: "text",
                maxLength: 500,
                placeholder: "example.com, partner.co.in",
                help: "Optional. Comma-separated. Leave empty to accept applications from any sender.",
            },
        ],
    },
    {
        key: "gmail",
        label: "Google Workspace / Gmail",
        description: "Watch a careers@ or jobs@ Google mailbox and import applications from it.",
        category: "intake",
        provisioning: "external_subscription",
        auth: "oauth",
        wired: false,
        prerequisite: "A Google Workspace account with the mailbox you want watched.",
        fields: [
            {
                key: "mailbox",
                label: "Mailbox address",
                type: "email",
                required: true,
                maxLength: 320,
                placeholder: "careers@yourcompany.com",
            },
            {
                key: "clientId",
                label: "OAuth client ID",
                type: "text",
                required: true,
                maxLength: 300,
                help: "From your Google Cloud project's OAuth credentials.",
            },
            {
                key: "clientSecret",
                label: "OAuth client secret",
                type: "password",
                required: true,
                secret: true,
                maxLength: 300,
            },
            {
                key: "label",
                label: "Only import from label",
                type: "text",
                maxLength: 120,
                placeholder: "Applications",
                help: "Optional. Leave empty to watch the whole inbox.",
            },
        ],
    },
    {
        key: "outlook",
        label: "Microsoft Outlook / 365",
        description: "Watch a Microsoft 365 shared mailbox and import applications from it.",
        category: "intake",
        provisioning: "external_subscription",
        auth: "oauth",
        wired: false,
        prerequisite: "A Microsoft 365 tenant and an app registration with Mail.Read on the mailbox.",
        fields: [
            {
                key: "mailbox",
                label: "Mailbox address",
                type: "email",
                required: true,
                maxLength: 320,
                placeholder: "careers@yourcompany.com",
            },
            { key: "tenantId", label: "Directory (tenant) ID", type: "text", required: true, maxLength: 200 },
            { key: "clientId", label: "Application (client) ID", type: "text", required: true, maxLength: 200 },
            {
                key: "clientSecret",
                label: "Client secret",
                type: "password",
                required: true,
                secret: true,
                maxLength: 300,
            },
        ],
    },
    {
        key: "send_mail",
        label: "Send mail",
        description: "Send candidate email through your own SMTP relay instead of TalentHop's sender.",
        category: "outbound",
        provisioning: "external_subscription",
        auth: "basic",
        wired: false,
        prerequisite: "An SMTP relay you control, with a sender address authorised to send for your domain.",
        fields: [
            { key: "host", label: "SMTP host", type: "text", required: true, maxLength: 255, placeholder: "smtp.yourcompany.com" },
            { key: "port", label: "Port", type: "number", required: true, min: 1, max: 65535, default: 587 },
            {
                key: "security",
                label: "Security",
                type: "select",
                required: true,
                default: "starttls",
                options: [
                    { value: "starttls", label: "STARTTLS (587)" },
                    { value: "tls", label: "TLS / SSL (465)" },
                    { value: "none", label: "None (not recommended)" },
                ],
            },
            { key: "username", label: "Username", type: "text", required: true, maxLength: 255 },
            { key: "password", label: "Password", type: "password", required: true, secret: true, maxLength: 300 },
            {
                key: "fromAddress",
                label: "From address",
                type: "email",
                required: true,
                maxLength: 320,
                help: "Must be authorised to send for your domain, or messages will be rejected or spam-filed.",
            },
        ],
    },
    {
        key: "webhook",
        label: "Inbound webhook",
        description: "POST candidate JSON from your career site, ATS or any custom source.",
        category: "ats",
        provisioning: "native",
        auth: "shared_secret",
        wired: false,
        fields: [
            {
                key: "signingSecret",
                label: "Signing secret",
                type: "password",
                required: true,
                secret: true,
                maxLength: 300,
                help: "We verify every POST against this. Generate a long random value and store it with your sender.",
            },
            {
                key: "defaultRequisitionId",
                label: "Default job",
                type: "text",
                maxLength: 64,
                help: "Optional. Used when a payload names no job.",
            },
        ],
    },
    {
        key: "crm",
        label: "CRM / ATS",
        description: "Push hiring outcomes to an external CRM or applicant tracking system.",
        category: "ats",
        provisioning: "external_subscription",
        auth: "api_key",
        wired: false,
        prerequisite: "An API-enabled plan on your CRM or ATS.",
        fields: [
            {
                key: "vendor",
                label: "System",
                type: "select",
                required: true,
                options: [
                    { value: "greenhouse", label: "Greenhouse" },
                    { value: "lever", label: "Lever" },
                    { value: "workday", label: "Workday" },
                    { value: "zoho", label: "Zoho Recruit" },
                    { value: "other", label: "Other / custom" },
                ],
            },
            { key: "baseUrl", label: "API base URL", type: "url", required: true, maxLength: 500 },
            { key: "apiKey", label: "API key", type: "password", required: true, secret: true, maxLength: 300 },
        ],
    },
    {
        key: "linkedin_jobs",
        label: "LinkedIn Jobs",
        description: "Publish jobs to LinkedIn and collect applicants from your own LinkedIn account.",
        category: "job_board",
        provisioning: "external_subscription",
        auth: "oauth",
        wired: false,
        prerequisite: "Your own LinkedIn Talent Solutions / Recruiter subscription. TalentHop does not resell LinkedIn job slots and cannot post without your account.",
        fields: [
            { key: "organizationId", label: "LinkedIn organisation ID", type: "text", required: true, maxLength: 120 },
            { key: "clientId", label: "OAuth client ID", type: "text", required: true, maxLength: 300 },
            { key: "clientSecret", label: "OAuth client secret", type: "password", required: true, secret: true, maxLength: 300 },
        ],
    },
    {
        key: "naukri",
        label: "Naukri",
        description: "Publish jobs to Naukri and collect applicants from your own Naukri account.",
        category: "job_board",
        provisioning: "external_subscription",
        auth: "api_key",
        wired: false,
        prerequisite: "Your own Naukri RecruiterConnect subscription and API access. TalentHop does not resell Naukri postings.",
        fields: [
            { key: "recruiterId", label: "Recruiter / company ID", type: "text", required: true, maxLength: 120 },
            { key: "apiKey", label: "API key", type: "password", required: true, secret: true, maxLength: 300 },
            { key: "baseUrl", label: "API base URL", type: "url", maxLength: 500, help: "Optional. Leave empty for the default endpoint." },
        ],
    },
];
const BY_KEY = new Map(CONNECTOR_TYPES.map((t) => [t.key, t]));
/** The type definition for a key, or undefined for anything not in the registry. */
export const connectorType = (key) => BY_KEY.get(key);
export const isConnectorTypeKey = (key) => BY_KEY.has(key);
/** The secret-marked field keys of a type — what the API must divert into `secrets`. */
export function secretFieldKeys(key) {
    return (connectorType(key)?.fields ?? []).filter((f) => f.secret).map((f) => f.key);
}
// ── job addresses ────────────────────────────────────────────────────────────
/**
 * Job codes use an UNAMBIGUOUS alphabet: no 0/O, 1/I/L or U. These codes are read off a screen
 * and typed into a mail client by a human forwarding a CV, so a code that can be mistyped is a
 * code that silently loses an application. Migration 0083 generates codes from this same set.
 */
export const JOB_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";
export const JOB_CODE_LENGTH = 6;
const JOB_CODE_RE = new RegExp(`^[${JOB_CODE_ALPHABET}]{${JOB_CODE_LENGTH}}$`);
export const isJobCode = (code) => JOB_CODE_RE.test(code);
/** `job-8F42K7@jobs.talenthopai.com` — the address a recruiter copies onto a job posting. */
export function jobEmailAddress(jobCode, domain = INBOUND_EMAIL_DOMAIN) {
    return `${JOB_EMAIL_PREFIX}${jobCode.toUpperCase()}@${domain}`;
}
/**
 * The job code carried by a recipient address, or null.
 *
 * Tolerant on purpose, because this runs against whatever a mail relay hands us:
 *   - a display form (`"Careers" <job-8F42K7@jobs.talenthopai.com>`) is unwrapped,
 *   - case is irrelevant (SMTP local-parts are case-preserving but nobody types them that way),
 *   - a `+tag` suffix (`job-8F42K7+ref@…`) is ignored,
 *   - the domain must match, so a look-alike address on someone else's domain is NOT accepted.
 *
 * Returns the canonical UPPER-CASE code, which is the form stored on the requisition.
 */
export function jobCodeFromAddress(address, domain = INBOUND_EMAIL_DOMAIN) {
    if (!address)
        return null;
    // Unwrap `Name <addr>` and strip stray quoting/whitespace.
    const angle = /<([^>]+)>/.exec(address);
    const bare = (angle ? angle[1] : address).trim().replace(/^["']|["']$/g, "");
    const at = bare.lastIndexOf("@");
    if (at <= 0)
        return null;
    const local = bare.slice(0, at);
    const host = bare.slice(at + 1).toLowerCase();
    if (host !== domain.toLowerCase())
        return null;
    if (!local.toLowerCase().startsWith(JOB_EMAIL_PREFIX))
        return null;
    // Drop a +tag suffix, then normalise.
    const code = local.slice(JOB_EMAIL_PREFIX.length).split("+")[0].toUpperCase();
    return isJobCode(code) ? code : null;
}
/**
 * The FIRST job code found across every address the message was delivered to.
 *
 * A forwarded application routinely carries the job address in Cc or in the envelope recipient
 * rather than in To — checking only To is how a real application gets filed nowhere.
 */
export function jobCodeFromRecipients(recipients, domain = INBOUND_EMAIL_DOMAIN) {
    for (const r of recipients) {
        if (!r)
            continue;
        // One header can hold several addresses.
        for (const part of r.split(",")) {
            const code = jobCodeFromAddress(part, domain);
            if (code)
                return code;
        }
    }
    return null;
}
/**
 * Validate a submitted config against its type's field definitions.
 *
 * `knownSecrets` names the secret fields ALREADY stored for this connector. A blank secret field
 * on an edit means "leave the stored one alone" (the adminProviders.ts idiom), so a required
 * secret that is already on file must not be reported missing — otherwise every edit of a saved
 * connector would demand the password be retyped.
 *
 * Returns [] when the config is usable. Unknown keys are reported rather than ignored: silently
 * dropping a field the caller believed it set is how a connector ends up configured differently
 * from what the screen showed.
 */
export function validateConnectorConfig(typeKey, config, knownSecrets = []) {
    const def = connectorType(typeKey);
    if (!def)
        return [{ field: "type", message: `Unknown connector type "${typeKey}"` }];
    const problems = [];
    const known = new Set(def.fields.map((f) => f.key));
    for (const key of Object.keys(config)) {
        if (!known.has(key))
            problems.push({ field: key, message: `"${key}" is not a field of ${def.label}` });
    }
    for (const field of def.fields) {
        const raw = config[field.key];
        const blank = raw === undefined || raw === null || (typeof raw === "string" && raw.trim() === "");
        if (blank) {
            // A stored secret satisfies its own requirement; anything else required must be present.
            if (field.required && !(field.secret && knownSecrets.includes(field.key))) {
                problems.push({ field: field.key, message: `${field.label} is required` });
            }
            continue;
        }
        switch (field.type) {
            case "boolean":
                if (typeof raw !== "boolean")
                    problems.push({ field: field.key, message: `${field.label} must be true or false` });
                break;
            case "number": {
                const n = typeof raw === "number" ? raw : Number(raw);
                if (!Number.isFinite(n)) {
                    problems.push({ field: field.key, message: `${field.label} must be a number` });
                }
                else if ((field.min !== undefined && n < field.min) || (field.max !== undefined && n > field.max)) {
                    problems.push({
                        field: field.key,
                        message: `${field.label} must be between ${field.min ?? "-∞"} and ${field.max ?? "∞"}`,
                    });
                }
                break;
            }
            case "select": {
                const allowed = (field.options ?? []).map((o) => o.value);
                if (typeof raw !== "string" || !allowed.includes(raw)) {
                    problems.push({ field: field.key, message: `${field.label} must be one of: ${allowed.join(", ")}` });
                }
                break;
            }
            case "email":
                if (typeof raw !== "string" || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim())) {
                    problems.push({ field: field.key, message: `${field.label} must be an email address` });
                }
                break;
            case "url":
                if (typeof raw !== "string" || !/^https?:\/\/[^\s]+$/i.test(raw.trim())) {
                    problems.push({ field: field.key, message: `${field.label} must be an http(s) URL` });
                }
                break;
            default:
                if (typeof raw !== "string") {
                    problems.push({ field: field.key, message: `${field.label} must be text` });
                }
                else if (field.maxLength && raw.length > field.maxLength) {
                    problems.push({ field: field.key, message: `${field.label} must be ${field.maxLength} characters or fewer` });
                }
        }
    }
    return problems;
}
/**
 * The status a connector should hold after a save.
 *
 * Deliberately conservative: a save never claims `connected` — only a passing test does that,
 * and only a test result is evidence the far end answered. Enabling a connector that has never
 * passed a test leaves it `configured`, so the pill cannot read "Active" for something that has
 * never successfully talked to anything.
 */
export function nextStatus(opts) {
    if (!opts.configComplete)
        return "not_configured";
    if (!opts.enabled)
        return "inactive";
    if (opts.lastTestFailed)
        return "error";
    if (opts.everConnected)
        return "active";
    return "configured";
}
// ── wire schemas ─────────────────────────────────────────────────────────────
/** A config object as posted. Values are scalars only — no nesting, so storage stays flat. */
export const connectorConfigSchema = z.record(z.string().max(64), z.union([z.string().max(2000), z.number(), z.boolean(), z.null()]));
export const connectorCreateSchema = z.object({
    typeKey: z.enum(CONNECTOR_TYPE_KEYS),
    name: z.string().trim().min(2).max(120).optional(),
    config: connectorConfigSchema.default({}),
    enabled: z.boolean().default(false),
});
export const connectorUpdateSchema = z
    .object({
    name: z.string().trim().min(2).max(120).optional(),
    config: connectorConfigSchema.optional(),
    enabled: z.boolean().optional(),
})
    .refine((v) => v.name !== undefined || v.config !== undefined || v.enabled !== undefined, {
    message: "Nothing to update",
});
/** PUT /admin/connectors/{typeKey} — the platform's availability switch for a whole type. */
export const connectorTypeAvailabilitySchema = z.object({
    enabled: z.boolean(),
    notes: z.string().trim().max(500).optional(),
});
// ── inbound email ────────────────────────────────────────────────────────────
/**
 * The stages an inbound application moves through, in order. Each one is PERSISTED as it
 * completes, so a message that fails at extraction is visibly "got as far as attachments
 * detected" rather than an opaque failure — and so a retry knows what is already done.
 */
export const INBOUND_STAGES = [
    "received",
    "job_identified",
    "candidate_created",
    "attachments_detected",
    "resume_extracted",
    "matched",
    "done",
];
export const INBOUND_STAGE_LABELS = {
    received: "Email received",
    job_identified: "Job identified",
    candidate_created: "Candidate created",
    attachments_detected: "Attachments detected",
    resume_extracted: "Resume extracted",
    matched: "AI matched",
    done: "Handed to recruiter",
};
/**
 * `needs_review` is the AI_FAIL_MODE=closed landing spot: the message arrived, we could not
 * finish it, and a human must look. It is never a silent drop and never a silent pass.
 */
export const INBOUND_STATUSES = ["pending", "processed", "needs_review", "failed"];
/** Hard ceiling per attachment — the same 10 MB docextract.ts already refuses to exceed. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;
/**
 * The normalised message an inbound relay posts to /inbound/email.
 *
 * Deliberately transport-agnostic: whatever collects the catch-all mailbox (a relay, a provider
 * webhook, or a future IMAP fetcher) parses MIME and posts THIS. That keeps the ingestion
 * pipeline — job routing, candidate creation, resume extraction — identical for every source and
 * testable without a mail server.
 */
export const inboundEmailSchema = z.object({
    /** Provider Message-ID. The idempotency key: the same message posted twice is stored once. */
    messageId: z.string().trim().min(1).max(500),
    /** The ORIGINAL recipient — the job address. This is what identifies the job. */
    to: z.string().trim().min(3).max(2000),
    /** Additional delivery addresses; a forwarded application often carries the job address here. */
    cc: z.string().trim().max(2000).optional(),
    /** Envelope recipient when the relay knows it — survives a rewritten To header. */
    envelopeTo: z.string().trim().max(320).optional(),
    from: z.string().trim().min(3).max(320),
    fromName: z.string().trim().max(200).optional(),
    /**
     * The From of an ORIGINAL message attached to this one ("Forward as attachment" keeps the real
     * headers, which an inline forward flattens and loses). Set by the collector only.
     */
    originalFrom: z.string().trim().max(320).optional(),
    originalFromName: z.string().trim().max(200).optional(),
    subject: z.string().trim().max(500).optional(),
    bodyText: z.string().max(200_000).optional(),
    /** ISO-8601, UTC ("…Z") or with an offset ("…+05:30"). Defaults to arrival time. */
    receivedAt: z.string().datetime({ offset: true }).optional(),
    attachments: z
        .array(z.object({
        filename: z.string().trim().min(1).max(300),
        mime: z.string().trim().min(1).max(200),
        contentBase64: z.string().min(1),
    }))
        .max(MAX_ATTACHMENTS)
        .default([]),
});
/** Extensions we treat as a resume when deciding whether an attachment is worth extracting. */
const RESUME_EXTS = new Set(["pdf", "doc", "docx", "rtf", "odt", "txt", "md"]);
const RESUME_MIMES = new Set([
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/rtf",
    "text/rtf",
    "application/vnd.oasis.opendocument.text",
    "text/plain",
    "text/markdown",
]);
/**
 * Is this attachment plausibly a CV?
 *
 * Extension OR mime, because mail clients routinely send `application/octet-stream` for a PDF.
 * Inline images (signatures, logos) are excluded — treating a signature block as a resume is how
 * an application ends up with a candidate named "logo".
 */
export function looksLikeResume(att) {
    const ext = att.filename.toLowerCase().split(".").pop() ?? "";
    if (RESUME_EXTS.has(ext))
        return true;
    return RESUME_MIMES.has(att.mime.toLowerCase().split(";")[0].trim());
}
/**
 * A display name for the applicant, best-effort and in priority order: the From display name,
 * then the local-part of the address made presentable. Never the filename — a CV called
 * `updated_cv_final2.pdf` is not a person's name.
 */
export function senderDisplayName(from, fromName) {
    const named = (fromName ?? "").trim().replace(/^["']|["']$/g, "");
    if (named && !named.includes("@"))
        return named.slice(0, 160);
    const local = from.split("@")[0] ?? "";
    const pretty = local
        .replace(/[._-]+/g, " ")
        .replace(/\d+/g, "")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    return (pretty || "Applicant").slice(0, 160);
}
/** Does this sender pass the connector's optional domain allow-list? Empty list = accept all. */
export function senderDomainAllowed(from, allowedDomains) {
    const list = (allowedDomains ?? "")
        .split(",")
        .map((d) => d.trim().toLowerCase().replace(/^@/, ""))
        .filter(Boolean);
    if (list.length === 0)
        return true;
    const domain = from.split("@")[1]?.toLowerCase() ?? "";
    return list.some((d) => domain === d || domain.endsWith(`.${d}`));
}
//# sourceMappingURL=connectors.js.map